import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { themeColor } from '../utils/themeColor';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { API_BASE_URL } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { chartTheme, useColorMode } from '../hooks/useColorMode';
import DayNotesEditor from './DayNotesEditor';
import './MyTradeHistory.css';

const MTH_STORAGE_KEY_VIEW = 'risklo_mth_view';
const MTH_STORAGE_KEY_DAY_NOTES = 'risklo_mth_day_notes';
const MTH_STORAGE_KEY_OVERALL_NOTES = 'risklo_mth_overall_notes';

const hasDayNote = (dayNotes, dateKey) => {
  const raw = dayNotes[dateKey];
  if (!raw || typeof raw !== 'string') return false;
  const stripped = raw.replace(/<[^>]+>/g, '').trim();
  return stripped.length > 0;
};
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const getMondayKey = (dateStr) => {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.getFullYear(), d.getMonth(), diff);
  return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
};

const ordinalSuffix = (n) => {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

const formatWeekLabel = (dateStr) => {
  if (!dateStr) return '';
  const [yyyy, mm, dd] = dateStr.split('-').map(Number);
  if (isNaN(mm) || isNaN(dd) || isNaN(yyyy)) return dateStr;
  const months = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'];
  return `Week of ${months[mm - 1] || ''} ${ordinalSuffix(dd)}, ${yyyy}`;
};

function MTHCumulativeChart({ chartData, formatCurrency }) {
  const chartColors = chartTheme(useColorMode());
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const chartHeight = isMobile ? 260 : 320;
  const chartMargins = isMobile ? { top: 8, right: 4, left: 0, bottom: 8 } : { top: 10, right: 20, left: 10, bottom: 10 };
  const tickFontSize = isMobile ? 10 : 11;
  const { yTicks, formatYTick } = useMemo(() => {
    if (!chartData || chartData.length === 0) return { yTicks: [], formatYTick: (v) => `$${v}` };
    const vals = chartData.map((d) => d.cumulative);
    const min = Math.min(0, ...vals);
    const max = Math.max(0, ...vals);
    const range = Math.max(max - min, 1);
    const step = range <= 500 ? 100 : range <= 2000 ? 500 : range <= 10000 ? 1000 : range <= 50000 ? 5000 : 10000;
    const ticks = [];
    let t = Math.floor(min / step) * step;
    while (t <= max + step) { ticks.push(t); t += step; }
    const yTicks = [...new Set(ticks)].sort((a, b) => a - b);
    const formatYTick = (v) => {
      if (Math.abs(v) < 1000) return `$${Math.round(v)}`;
      const k = v / 1000;
      return `$${Math.abs(k) % 1 === 0 ? k : k.toFixed(1)}K`;
    };
    return { yTicks, formatYTick };
  }, [chartData]);
  if (!chartData || chartData.length === 0) {
    return (
      <div className="mth-chart-empty">
        <p>No daily data available to display cumulative P&L.</p>
      </div>
    );
  }
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    const dailyColor = (p.dailyPnl ?? 0) >= 0 ? '#10b981' : '#ef4444';
    return (
      <div className="mth-chart-tooltip">
        <div>{p.tooltipDate}</div>
        <div style={{ color: 'rgba(102, 126, 234, 1)', fontWeight: 600 }}>Cumulative: {formatCurrency(p.cumulative)}</div>
        <div style={{ color: dailyColor, fontSize: '0.85em', marginTop: '0.25rem' }}>That day: {formatCurrency(p.dailyPnl)}</div>
      </div>
    );
  };
  return (
    <div className="mth-chart-wrapper">
      <div className="mth-chart-header">
        <h4 className="mth-chart-title">Daily net cumulative P&L</h4>
        <span className="mth-chart-info" title="Cumulative profit/loss over time">ⓘ</span>
      </div>
      <div className="mth-chart-container">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <AreaChart data={chartData} margin={chartMargins}>
            <defs>
              <linearGradient id="mthChartGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={themeColor('#667eea')} stopOpacity={0.4} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} vertical={false} />
            <XAxis dataKey="dateLabel" stroke={chartColors.axis} tick={{ fontSize: tickFontSize, fill: chartColors.tick }} />
            <YAxis stroke={chartColors.axis} tick={{ fontSize: tickFontSize, fill: chartColors.tick }} ticks={yTicks} tickFormatter={formatYTick} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="cumulative" stroke={themeColor('#667eea')} strokeWidth={2} fill="url(#mthChartGradient)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function getDateRange(filter, customStart, customEnd) {
  const now = new Date();
  let startDate = null;
  let endDate = null;
  switch (filter) {
    case 'all-time':
      return { startDate: null, endDate: null };
    case 'last-30-days':
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
      endDate = now;
      break;
    case 'last-3-months':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 3);
      endDate = now;
      break;
    case 'last-6-months':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 6);
      endDate = now;
      break;
    case 'last-12-months':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 12);
      endDate = now;
      break;
    case '2024':
      startDate = new Date(2024, 0, 1);
      endDate = new Date(2024, 11, 31);
      break;
    case '2025':
      startDate = new Date(2025, 0, 1);
      endDate = new Date(2025, 11, 31);
      break;
    case '2026':
      startDate = new Date(2026, 0, 1);
      endDate = new Date(2026, 11, 31);
      break;
    case 'custom':
      if (customStart) startDate = new Date(customStart);
      if (customEnd) endDate = new Date(customEnd);
      break;
    default:
      return { startDate: null, endDate: null };
  }
  return { startDate, endDate };
}

function MyTradeHistory({
  user, connections, riskAssessments, formatCurrency,
  dateRangeFilter = null, customStartDate = null, customEndDate = null,
  selectedAccountNames = null,
  onAccountClick = null,
  getUsableStrategies = null,
  onOpenStrategyModal = null
}) {
  const [allTrades, setAllTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [calendarRange, setCalendarRange] = useState('month');
  const [expandedDay, setExpandedDay] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    try {
      const stored = localStorage.getItem(MTH_STORAGE_KEY_VIEW);
      if (['calendar', 'chart', 'weekly'].includes(stored)) return stored;
    } catch (_) {}
    return 'calendar';
  });
  const [internalDateRangeFilter] = useState(() => {
    try {
      return localStorage.getItem('mth.dateRangeFilter') || 'all-time';
    } catch (_) { return 'all-time'; }
  });
  const [internalCustomStart] = useState(() => {
    try { return localStorage.getItem('mth.customStartDate') || ''; } catch (_) { return ''; }
  });
  const [internalCustomEnd] = useState(() => {
    try { return localStorage.getItem('mth.customEndDate') || ''; } catch (_) { return ''; }
  });
  const [dayNotes, setDayNotes] = useState(() => {
    try {
      const raw = localStorage.getItem(MTH_STORAGE_KEY_DAY_NOTES);
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    return {};
  });
  const [overallNote, setOverallNote] = useState(() => {
    try {
      return localStorage.getItem(MTH_STORAGE_KEY_OVERALL_NOTES) || '';
    } catch (_) { return ''; }
  });
  const [showOverallNotesModal, setShowOverallNotesModal] = useState(false);

  useEffect(() => {
    if (!showOverallNotesModal) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowOverallNotesModal(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showOverallNotesModal]);
  const effectiveDateRangeFilter = dateRangeFilter ?? internalDateRangeFilter;
  const effectiveCustomStart = customStartDate ?? internalCustomStart;
  const effectiveCustomEnd = customEndDate ?? internalCustomEnd;
  const calendarHeaderRef = useRef(null);

  // Fetch ALL historical trades
  const fetchAllTrades = useCallback(async () => {
    if (!user?.email) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/ninjatrader/trades?userEmail=${encodeURIComponent(user.email)}`,
        { headers: getBearerAuthHeaders() }
      );
      if (!response.ok) throw new Error('Failed to fetch trades');
      const data = await response.json();
      setAllTrades(data.trades || []);
    } catch (err) {
      console.error('Error fetching trade history:', err);
      setAllTrades([]);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    fetchAllTrades();
  }, [fetchAllTrades]);

  useEffect(() => {
    if (dateRangeFilter == null) {
      try { localStorage.setItem('mth.dateRangeFilter', internalDateRangeFilter); } catch (_) {}
    }
  }, [dateRangeFilter, internalDateRangeFilter]);

  useEffect(() => {
    if (customStartDate == null) {
      try { localStorage.setItem('mth.customStartDate', internalCustomStart); } catch (_) {}
    }
  }, [customStartDate, internalCustomStart]);

  useEffect(() => {
    if (customEndDate == null) {
      try { localStorage.setItem('mth.customEndDate', internalCustomEnd); } catch (_) {}
    }
  }, [customEndDate, internalCustomEnd]);

  // Filter trades by date range and account selection
  const filteredTrades = useMemo(() => {
    let trades = allTrades;
    const { startDate, endDate } = getDateRange(effectiveDateRangeFilter, effectiveCustomStart, effectiveCustomEnd);
    if (startDate || endDate) {
      trades = trades.filter(trade => {
        const d = new Date(trade.entry_time);
        if (startDate && d < startDate) return false;
        if (endDate) {
          const dayEnd = new Date(d);
          dayEnd.setHours(23, 59, 59, 999);
          if (dayEnd > endDate) return false;
        }
        return true;
      });
    }
    if (selectedAccountNames && Array.isArray(selectedAccountNames) && selectedAccountNames.length > 0) {
      const set = new Set(selectedAccountNames);
      trades = trades.filter(t => set.has(t.account_name));
    }
    return trades;
  }, [allTrades, effectiveDateRangeFilter, effectiveCustomStart, effectiveCustomEnd, selectedAccountNames]);

  // Scroll to calendar header (month picker) when day is expanded so results are visible below
  useEffect(() => {
    if (expandedDay && calendarHeaderRef.current) {
      calendarHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [expandedDay]);

  // Set of account names excluded from metrics (Accounts view source of truth)
  const excludedAccountNames = useMemo(() => {
    const set = new Set();
    if (connections) {
      connections.forEach(c => {
        if (c.exclude_from_metrics) set.add(c.account_name);
      });
    }
    return set;
  }, [connections]);

  // Helper: get YYYY-MM-DD in Eastern Time (US futures trading day)
  const getDateKeyET = useCallback((dateOrString) => {
    const d = new Date(dateOrString);
    const formatter = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' });
    const parts = formatter.formatToParts(d);
    const y = parts.find(p => p.type === 'year').value;
    const m = parts.find(p => p.type === 'month').value;
    const day = parts.find(p => p.type === 'day').value;
    return `${y}-${m}-${day}`;
  }, []);

  // Process trades into daily data: Map<"YYYY-MM-DD", { totalPnl, trades, accounts, accountBreakdown }>
  // Uses Eastern Time for day assignment (US market) so late-night trades land on correct trading day.
  // Excluded accounts (from Accounts view) are in accountBreakdown for display but NOT in metrics (totalPnl, trades, etc.)
  const dailyData = useMemo(() => {
    const map = new Map();
    filteredTrades.forEach(trade => {
      const key = getDateKeyET(trade.entry_time);
      if (!map.has(key)) {
        map.set(key, { totalPnl: 0, trades: 0, accounts: new Set(), accountBreakdown: {}, winningTrades: 0, losingTrades: 0 });
      }
      const day = map.get(key);
      const pnl = parseFloat(trade.net_pnl) || 0;
      const isExcluded = excludedAccountNames.has(trade.account_name);
      if (!isExcluded) {
        day.totalPnl += pnl;
        day.trades += 1;
        day.accounts.add(trade.account_name);
        if (pnl > 0) day.winningTrades++;
        if (pnl < 0) day.losingTrades++;
      }
      if (!day.accountBreakdown[trade.account_name]) {
        day.accountBreakdown[trade.account_name] = {
          pnl: 0, trades: 0, tradeList: [], strategies: new Set(), instruments: new Set(), wins: 0, losses: 0
        };
      }
      const acct = day.accountBreakdown[trade.account_name];
      acct.pnl += pnl;
      acct.trades += 1;
      acct.tradeList.push(trade);
      if (pnl > 0) acct.wins++;
      if (pnl < 0) acct.losses++;
      if (trade.strategy_name) acct.strategies.add(trade.strategy_name);
      if (trade.instrument) acct.instruments.add(trade.instrument);
    });

    // For today (ET) only, prefer account-level realized P&L snapshots when available.
    // realized_pnl from the connector reflects current session/day - do NOT apply to past days.
    const todayET = (() => {
      const formatter = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' });
      const parts = formatter.formatToParts(new Date());
      const y = parts.find(p => p.type === 'year').value;
      const m = parts.find(p => p.type === 'month').value;
      const d = parts.find(p => p.type === 'day').value;
      return `${y}-${m}-${d}`;
    })();
    const sortedKeys = Array.from(map.keys()).sort();
    const latestDayKey = sortedKeys.length > 0 ? sortedKeys[sortedKeys.length - 1] : null;
    const isLatestDayToday = latestDayKey === todayET;
    if (isLatestDayToday && latestDayKey && Array.isArray(connections) && connections.length > 0) {
      const latestDay = map.get(latestDayKey);
      if (latestDay && latestDay.accountBreakdown) {
        const connMap = {};
        connections.forEach((c) => {
          if (c?.account_name) connMap[c.account_name] = c;
        });

        let overrideTotal = 0;
        let hasAtLeastOneSnapshot = false;

        Object.keys(latestDay.accountBreakdown).forEach((acctName) => {
          const conn = connMap[acctName];
          const realized = Number(conn?.realized_pnl);
          if (Number.isFinite(realized)) {
            latestDay.accountBreakdown[acctName].pnl = realized;
            hasAtLeastOneSnapshot = true;
            if (!excludedAccountNames.has(acctName)) {
              overrideTotal += realized;
            }
          }
        });

        if (hasAtLeastOneSnapshot) {
          latestDay.totalPnl = overrideTotal;
        }
      }
    }
    return map;
  }, [filteredTrades, excludedAccountNames, connections, getDateKeyET]);

  // Build a map of connection data for account info
  const connectionMap = useMemo(() => {
    const map = {};
    if (connections) {
      connections.forEach(c => {
        map[c.account_name] = c;
      });
    }
    return map;
  }, [connections]);

  // Get account size and max drawdown for an account (from connection + risk assessment)
  const getAccountRiskParams = useCallback((acctName) => {
    const conn = connectionMap[acctName];
    const risk = riskAssessments?.find(a => a.accountName === acctName);
    const currentBalance = parseFloat(conn?.starting_balance) || 50000;
    let accountSize = risk?.accountSize;
    if (accountSize == null) {
      if (conn?.account_type === 'live' && conn?.custom_starting_balance != null) {
        accountSize = parseFloat(conn.custom_starting_balance);
      } else {
        if (currentBalance >= 295000) accountSize = 300000;
        else if (currentBalance >= 245000) accountSize = 250000;
        else if (currentBalance >= 145000) accountSize = 150000;
        else if (currentBalance >= 95000) accountSize = 100000;
        else if (currentBalance >= 45000) accountSize = 50000;
        else accountSize = 25000;
      }
    }
    let maxDrawdown = risk?.maxDrawdown;
    if (maxDrawdown == null && conn) {
      const td = parseFloat(conn.trailing_max_drawdown);
      if (td > 0) maxDrawdown = td;
      else {
        if (accountSize >= 300000) maxDrawdown = 8000;
        else if (accountSize >= 250000) maxDrawdown = 7000;
        else if (accountSize >= 150000) maxDrawdown = 7500;
        else if (accountSize >= 100000) maxDrawdown = 5000;
        else if (accountSize >= 50000) maxDrawdown = 2500;
        else maxDrawdown = 1250;
      }
    }
    const maxMaePerTrade = risk?.strategies?.[0]?.apexMaeComparison?.maxMaePerTrade != null
      ? parseFloat(risk.strategies[0].apexMaeComparison.maxMaePerTrade)
      : null;
    return { currentBalance, accountSize, maxDrawdown, maxMaePerTrade };
  }, [connectionMap, riskAssessments]);

  // ACTUAL-DAY alerts: based on real P&L that day, not probability from Risk Management
  const actualDayAlerts = useMemo(() => {
    const map = {};
    if (!allTrades.length || !connections?.length) return map;

    const tradesByAccount = {};
    allTrades.forEach(t => {
      const name = t.account_name;
      if (!tradesByAccount[name]) tradesByAccount[name] = [];
      tradesByAccount[name].push({ ...t, pnl: parseFloat(t.net_pnl) || 0 });
    });
    Object.keys(tradesByAccount).forEach(name => {
      tradesByAccount[name].sort((a, b) => new Date(a.entry_time) - new Date(b.entry_time));
    });

    dailyData.forEach((day, dateKey) => {
      map[dateKey] = {};
      day.accounts.forEach(acctName => {
        const params = getAccountRiskParams(acctName);
        const { currentBalance, accountSize, maxDrawdown, maxMaePerTrade } = params;
        if (accountSize == null || maxDrawdown == null) return;

        const allAcctTrades = tradesByAccount[acctName] || [];
        const startOfDayBalance = currentBalance - allAcctTrades
          .filter(t => {
            const d = new Date(t.entry_time);
            const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            return k >= dateKey;
          })
          .reduce((s, t) => s + t.pnl, 0);

        const blowoutThreshold = accountSize - maxDrawdown;
        const dayTrades = (day.accountBreakdown[acctName]?.tradeList || [])
          .map(t => ({ ...t, pnl: parseFloat(t.net_pnl) || 0 }))
          .sort((a, b) => new Date(a.entry_time) - new Date(b.entry_time));

        let runningEquity = startOfDayBalance;
        let minEquity = runningEquity;
        for (const t of dayTrades) {
          runningEquity += t.pnl;
          if (runningEquity < minEquity) minEquity = runningEquity;
        }
        const blowout = minEquity < blowoutThreshold;

        let thirtyRule = false;
        if (maxMaePerTrade != null && maxMaePerTrade > 0) {
          for (const t of dayTrades) {
            if (t.pnl < 0 && Math.abs(t.pnl) > maxMaePerTrade) {
              thirtyRule = true;
              break;
            }
          }
        }

        map[dateKey][acctName] = {
          blowout,
          thirtyRule,
          accountSize,
          maxDrawdown,
          startOfDayBalance,
          minEquity,
          blowoutThreshold
        };
      });
    });
    return map;
  }, [allTrades, connections, dailyData, getAccountRiskParams]);

  // Overall metrics
  const overallMetrics = useMemo(() => {
    if (dailyData.size === 0) return null;
    let totalPnl = 0;
    let totalTrades = 0;
    let winningDays = 0;
    let losingDays = 0;
    let bestDay = { key: '', pnl: -Infinity };
    let worstDay = { key: '', pnl: Infinity };
    let totalWinningTrades = 0;
    let totalLosingTrades = 0;
    const allAccounts = new Set();

    dailyData.forEach((day, key) => {
      totalPnl += day.totalPnl;
      totalTrades += day.trades;
      totalWinningTrades += day.winningTrades;
      totalLosingTrades += day.losingTrades;
      day.accounts.forEach(a => allAccounts.add(a));
      if (day.totalPnl > 0) winningDays++;
      if (day.totalPnl < 0) losingDays++;
      if (day.totalPnl > bestDay.pnl) bestDay = { key, pnl: day.totalPnl };
      if (day.totalPnl < worstDay.pnl) worstDay = { key, pnl: day.totalPnl };
    });

    const tradingDays = winningDays + losingDays;
    const dayWinRate = tradingDays > 0 ? ((winningDays / tradingDays) * 100).toFixed(1) : '0.0';
    const tradeWinRate = (totalWinningTrades + totalLosingTrades) > 0
      ? ((totalWinningTrades / (totalWinningTrades + totalLosingTrades)) * 100).toFixed(1)
      : '0.0';
    const avgDailyPnl = tradingDays > 0 ? totalPnl / tradingDays : 0;

    return {
      totalPnl, totalTrades, winningDays, losingDays, tradingDays,
      dayWinRate, tradeWinRate, avgDailyPnl,
      bestDay, worstDay,
      totalAccounts: allAccounts.size
    };
  }, [dailyData]);

  // Monthly metrics (for current month view)
  const monthMetrics = useMemo(() => {
    const { year, month } = calendarMonth;
    let totalPnl = 0;
    let totalTrades = 0;
    let tradingDays = 0;

    dailyData.forEach((day, key) => {
      const d = new Date(key + 'T12:00:00');
      if (d.getFullYear() === year && d.getMonth() === month) {
        totalPnl += day.totalPnl;
        totalTrades += day.trades;
        if (day.trades > 0) tradingDays++;
      }
    });

    return { totalPnl, totalTrades, tradingDays };
  }, [dailyData, calendarMonth]);

  // Year stats for year view
  const yearStats = useMemo(() => {
    const stats = Array.from({ length: 12 }, () => ({ totalPnl: 0, trades: 0, days: 0, accounts: new Set() }));
    dailyData.forEach((day, key) => {
      const d = new Date(key + 'T12:00:00');
      if (d.getFullYear() === calendarMonth.year) {
        const m = d.getMonth();
        stats[m].totalPnl += day.totalPnl;
        stats[m].trades += day.trades;
        stats[m].days++;
        day.accounts.forEach(a => stats[m].accounts.add(a));
      }
    });
    return stats;
  }, [dailyData, calendarMonth.year]);

  // Weekly summary for calendar month (sidebar) - group days by week (Monday start)
  const monthWeeksSummary = useMemo(() => {
    const { year, month } = calendarMonth;
    const byWeek = new Map();
    dailyData.forEach((day, key) => {
      const d = new Date(key + 'T12:00:00');
      if (d.getFullYear() !== year || d.getMonth() !== month) return;
      const mondayKey = getMondayKey(key);
      if (!byWeek.has(mondayKey)) byWeek.set(mondayKey, { total: 0, daysCount: 0 });
      const w = byWeek.get(mondayKey);
      w.total += day.totalPnl;
      w.daysCount += 1;
    });
    return Array.from(byWeek.entries())
      .map(([mondayKey, { total, daysCount }]) => ({ mondayKey, label: formatWeekLabel(mondayKey), total, daysCount }))
      .sort((a, b) => a.mondayKey.localeCompare(b.mondayKey));
  }, [dailyData, calendarMonth]);

  // Weekly data for Weekly view - all weeks with day breakdown
  const weeklyData = useMemo(() => {
    const byWeek = new Map();
    dailyData.forEach((day, key) => {
      const mondayKey = getMondayKey(key);
      if (!byWeek.has(mondayKey)) {
        byWeek.set(mondayKey, { mondayKey, days: { Sun: 0, Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0 }, total: 0 });
      }
      const w = byWeek.get(mondayKey);
      const d = new Date(key + 'T12:00:00');
      const dayName = DAY_NAMES[d.getDay()];
      w.days[dayName] = day.totalPnl;
      w.total += day.totalPnl;
    });
    return Array.from(byWeek.values())
      .sort((a, b) => b.mondayKey.localeCompare(a.mondayKey));
  }, [dailyData]);

  // Chart data - cumulative P&L over time
  const chartData = useMemo(() => {
    const entries = Array.from(dailyData.entries())
      .filter(([, day]) => day.trades > 0)
      .sort((a, b) => a[0].localeCompare(b[0]));
    let cumulative = 0;
    return entries.map(([date, day]) => {
      cumulative += day.totalPnl;
      const [yyyy, mm, dd] = date.split('-');
      const dateLabel = `${mm}/${dd}/${String(yyyy).slice(-2)}`;
      const tooltipDate = `${mm}/${dd}/${yyyy}`;
      return { date, dateLabel, tooltipDate, cumulative, dailyPnl: day.totalPnl };
    });
  }, [dailyData]);

  // Calendar grid cells
  const calendarCells = useMemo(() => {
    const { year, month } = calendarMonth;
    const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];

    // Padding cells
    for (let i = 0; i < firstDay; i++) {
      cells.push({ type: 'pad', key: `pad-${i}` });
    }

    // Day cells
    for (let d = 1; d <= daysInMonth; d++) {
      const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const data = dailyData.get(key) || null;
      cells.push({ type: 'day', day: d, key, data });
    }

    return cells;
  }, [calendarMonth, dailyData]);

  // Navigation
  const goPrev = () => {
    if (calendarRange === 'month') {
      setCalendarMonth(prev => {
        const m = prev.month - 1;
        return m < 0 ? { year: prev.year - 1, month: 11 } : { year: prev.year, month: m };
      });
    } else {
      setCalendarMonth(prev => ({ ...prev, year: prev.year - 1 }));
    }
    setExpandedDay(null);
  };

  const goNext = () => {
    if (calendarRange === 'month') {
      setCalendarMonth(prev => {
        const m = prev.month + 1;
        return m > 11 ? { year: prev.year + 1, month: 0 } : { year: prev.year, month: m };
      });
    } else {
      setCalendarMonth(prev => ({ ...prev, year: prev.year + 1 }));
    }
    setExpandedDay(null);
  };

  const goToday = () => {
    const now = new Date();
    setCalendarMonth({ year: now.getFullYear(), month: now.getMonth() });
    setExpandedDay(null);
  };

  // Earliest trade date for "Jump to first trade" when All Time is selected (uses ET for consistency)
  const earliestTradeKey = useMemo(() => {
    if (filteredTrades.length === 0) return null;
    let earliest = filteredTrades[0];
    for (const t of filteredTrades) {
      if (new Date(t.entry_time) < new Date(earliest.entry_time)) earliest = t;
    }
    return getDateKeyET(earliest.entry_time);
  }, [filteredTrades, getDateKeyET]);

  const goToFirstTrade = useCallback(() => {
    if (!earliestTradeKey) return;
    const [y, m] = earliestTradeKey.split('-').map(Number);
    setCalendarMonth({ year: y, month: m - 1 });
    setExpandedDay(null);
  }, [earliestTradeKey]);

  const saveOverallNote = useCallback((text) => {
    setOverallNote(text);
    try {
      localStorage.setItem(MTH_STORAGE_KEY_OVERALL_NOTES, text || '');
    } catch (_) {}
  }, []);

  const saveDayNote = useCallback((dateKey, text) => {
    setDayNotes(prev => {
      const next = { ...prev };
      if (text && text.trim()) next[dateKey] = text;
      else delete next[dateKey];
      try {
        const toStore = {};
        Object.keys(next).forEach(k => { if (next[k]?.trim()) toStore[k] = next[k].trim(); });
        localStorage.setItem(MTH_STORAGE_KEY_DAY_NOTES, JSON.stringify(toStore));
      } catch (_) {}
      return next;
    });
  }, []);

  // Check if a day has any accounts with ACTUAL-DAY risk alerts (not probability-based)
  const getDayAlerts = (dayData, dateKey) => {
    if (!dayData || !dateKey) return { hasBlowout: false, has30Pct: false };
    const dayAlerts = actualDayAlerts[dateKey] || {};
    let hasBlowout = false;
    let has30Pct = false;
    dayData.accounts.forEach(acctName => {
      const a = dayAlerts[acctName];
      if (a) {
        if (a.blowout) hasBlowout = true;
        if (a.thirtyRule) has30Pct = true;
      }
    });
    return { hasBlowout, has30Pct };
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayHeaders = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  const formatCompactCurrency = (num) => {
    const abs = Math.abs(num);
    const sign = num < 0 ? '-' : '';
    if (abs >= 1000) {
      const k = abs / 1000;
      return `${sign}$${k % 1 === 0 ? k : k.toFixed(1)}K`;
    }
    return `${sign}$${abs.toFixed(0)}`;
  };

  const formatDate = (key) => {
    const d = new Date(key + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="mth-container">
        <div className="mth-loading">
          <div className="mth-spinner"></div>
          <p>Loading trade history...</p>
        </div>
      </div>
    );
  }

  if (allTrades.length === 0) {
    return (
      <div className="mth-container">
        <div className="mth-empty">
          <h3>No Trade History</h3>
          <p>Sync your NinjaTrader accounts to see trade history here.</p>
        </div>
      </div>
    );
  }

  const hasDateFilter = effectiveDateRangeFilter !== 'all-time';

  return (
    <div className="mth-container">
      {/* No data for date range */}
      {hasDateFilter && filteredTrades.length === 0 && (
        <div className="mth-no-range-data">
          No trades in selected date range.
        </div>
      )}

      {/* Overall Metrics */}
      {overallMetrics && (
        <div className="mth-metrics-grid">
          <div className="mth-metric-card">
            <div className="mth-metric-label">{hasDateFilter ? 'Range P&L' : 'All-Time P&L'}</div>
            <div className={`mth-metric-value ${overallMetrics.totalPnl >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(overallMetrics.totalPnl)}
            </div>
          </div>
          <div className="mth-metric-card">
            <div className="mth-metric-label">Total Trades</div>
            <div className="mth-metric-value">{overallMetrics.totalTrades.toLocaleString()}</div>
          </div>
          <div className="mth-metric-card">
            <div className="mth-metric-label">Day Win Rate</div>
            <div className="mth-metric-value">{overallMetrics.dayWinRate}%</div>
            <div className="mth-metric-sub">{overallMetrics.winningDays}W / {overallMetrics.losingDays}L</div>
          </div>
          <div className="mth-metric-card">
            <div className="mth-metric-label">Trade Win Rate</div>
            <div className="mth-metric-value">{overallMetrics.tradeWinRate}%</div>
          </div>
          <div className="mth-metric-card">
            <div className="mth-metric-label">Avg Daily P&L</div>
            <div className={`mth-metric-value ${overallMetrics.avgDailyPnl >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(overallMetrics.avgDailyPnl)}
            </div>
          </div>
          <div className="mth-metric-card">
            <div className="mth-metric-label">Best Day</div>
            <div className="mth-metric-value positive">{formatCurrency(overallMetrics.bestDay.pnl)}</div>
            <div className="mth-metric-sub">{overallMetrics.bestDay.key ? formatDate(overallMetrics.bestDay.key) : '-'}</div>
          </div>
          <div className="mth-metric-card">
            <div className="mth-metric-label">Worst Day</div>
            <div className="mth-metric-value negative">{formatCurrency(overallMetrics.worstDay.pnl)}</div>
            <div className="mth-metric-sub">{overallMetrics.worstDay.key ? formatDate(overallMetrics.worstDay.key) : '-'}</div>
          </div>
          <div className="mth-metric-card">
            <div className="mth-metric-label">Accounts Traded</div>
            <div className="mth-metric-value">{overallMetrics.totalAccounts}</div>
          </div>
        </div>
      )}

      {/* View toggle + Calendar Header */}
      <div className="mth-view-header">
        <div className="mth-view-toggle">
          <button
            className={`mth-view-btn ${viewMode === 'calendar' ? 'active' : ''}`}
            onClick={() => { setViewMode('calendar'); try { localStorage.setItem(MTH_STORAGE_KEY_VIEW, 'calendar'); } catch (_) {} }}
          >
            Calendar
          </button>
          <button
            className={`mth-view-btn ${viewMode === 'chart' ? 'active' : ''}`}
            onClick={() => { setViewMode('chart'); try { localStorage.setItem(MTH_STORAGE_KEY_VIEW, 'chart'); } catch (_) {} }}
          >
            Chart
          </button>
          <button
            className={`mth-view-btn ${viewMode === 'weekly' ? 'active' : ''}`}
            onClick={() => { setViewMode('weekly'); try { localStorage.setItem(MTH_STORAGE_KEY_VIEW, 'weekly'); } catch (_) {} }}
          >
            Weekly
          </button>
          <button
            type="button"
            className="mth-overall-notes-btn"
            onClick={() => setShowOverallNotesModal(true)}
            title="Overall notes"
          >
            📝
          </button>
        </div>
      </div>

      {/* Overall Notes Modal */}
      {showOverallNotesModal && (
        <div className="mth-modal-backdrop" onClick={() => setShowOverallNotesModal(false)}>
          <div className="mth-overall-notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mth-overall-notes-modal-header">
              <h4>Overall Notes</h4>
              <button type="button" className="mth-overall-notes-modal-close" onClick={() => setShowOverallNotesModal(false)}>✕</button>
            </div>
            <div className="mth-overall-notes-modal-body">
              <DayNotesEditor
                dateKey="overall"
                value={overallNote}
                onChange={saveOverallNote}
                placeholder="Notes across all days..."
              />
            </div>
          </div>
        </div>
      )}

      {viewMode === 'chart' ? (
        <MTHCumulativeChart chartData={chartData} formatCurrency={formatCurrency} />
      ) : viewMode === 'weekly' ? (
        <div className="mth-weeks-grid">
          {weeklyData.map((week, idx) => (
            <div key={idx} className={`mth-week-card ${week.total >= 0 ? 'win' : 'loss'}`}>
              <div className="mth-week-header">
                <span className="mth-week-date">{formatWeekLabel(week.mondayKey)}</span>
                <span className={`mth-week-total ${week.total >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(week.total)}
                </span>
              </div>
              <div className="mth-week-days">
                {DAY_NAMES.map((dayName) => {
                  const value = week.days[dayName] ?? 0;
                  if (value === 0) return null;
                  return (
                    <div key={dayName} className="mth-day-item">
                      <span className="mth-day-label">{dayName}</span>
                      <span className={`mth-day-value ${value >= 0 ? 'positive' : 'negative'}`}>
                        {formatCurrency(value)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
      {/* Calendar Header - scroll target when day is expanded */}
      <div ref={calendarHeaderRef} className="mth-calendar-header">
        <div className="mth-calendar-nav">
          <button className="mth-nav-btn" onClick={goPrev}>←</button>
          <h3 className="mth-calendar-title">
            {calendarRange === 'month'
              ? `${monthNames[calendarMonth.month]} ${calendarMonth.year}`
              : `${calendarMonth.year}`
            }
          </h3>
          <button className="mth-nav-btn" onClick={goNext}>→</button>
        </div>
        <div className="mth-calendar-controls">
          {calendarRange === 'month' && monthMetrics && (
            <div className="mth-month-stats">
              <span className={`mth-month-pnl ${monthMetrics.totalPnl >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(monthMetrics.totalPnl)}
              </span>
              <span className="mth-month-info">{monthMetrics.totalTrades} trades · {monthMetrics.tradingDays} days</span>
            </div>
          )}
          <div className="mth-range-controls">
            <button className="mth-today-btn" onClick={goToday}>Today</button>
            {effectiveDateRangeFilter === 'all-time' && earliestTradeKey && (
              <button className="mth-first-trade-btn" onClick={goToFirstTrade} title="Jump to month of first trade">
                First trade
              </button>
            )}
            <select
              className="mth-range-select"
              value={calendarRange}
              onChange={(e) => setCalendarRange(e.target.value)}
            >
              <option value="month">This month</option>
              <option value="year">This year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Calendar Body - with Weekly Summary sidebar when month view */}
      {calendarRange === 'month' ? (
        <div className="mth-calendar-with-sidebar">
        <div className="mth-calendar-month">
          <div className="mth-calendar-grid">
            {dayHeaders.map(d => (
              <div key={d} className="mth-day-header">{d}</div>
            ))}
            {calendarCells.map((cell) => {
              if (cell.type === 'pad') {
                return <div key={cell.key} className="mth-cell empty" />;
              }
              const { day, key, data } = cell;
              const alerts = getDayAlerts(data, key);
              const isExpanded = expandedDay === key;
              const hasData = data && data.trades > 0;
              const tone = hasData ? (data.totalPnl >= 0 ? 'profit' : 'loss') : '';

              const hasNote = hasDayNote(dayNotes, key);
              return (
                <div
                  key={key}
                  className={`mth-cell ${tone} ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => hasData && setExpandedDay(isExpanded ? null : key)}
                >
                  <span className="mth-cell-day">{day}</span>
                  {(alerts.hasBlowout || alerts.has30Pct) && (
                    <div className="mth-cell-alerts">
                      {alerts.hasBlowout && <span className="mth-alert-icon blowout" title="Account at blowout risk">💥</span>}
                      {alerts.has30Pct && <span className="mth-alert-icon thirty-pct" title="% rule exceeded">⚠️</span>}
                    </div>
                  )}
                  {hasData && (
                    <div className="mth-cell-content">
                      <span className={`mth-cell-pnl ${data.totalPnl >= 0 ? 'positive' : 'negative'}`}>
                        {formatCompactCurrency(data.totalPnl)}
                      </span>
                      <span className="mth-cell-trades">{data.trades} trade{data.trades !== 1 ? 's' : ''}</span>
                      <span className="mth-cell-accounts">{data.accounts.size} acct{data.accounts.size !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {hasNote && (
                    <span className="mth-cell-note-icon" title="Has notes">📝</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Expanded Day Detail */}
          {expandedDay && dailyData.has(expandedDay) && (
            <DayDetail
              dateKey={expandedDay}
              dayData={dailyData.get(expandedDay)}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              actualDayAlerts={actualDayAlerts}
              connectionMap={connectionMap}
              dayNote={dayNotes[expandedDay] ?? ''}
              onDayNoteChange={(text) => saveDayNote(expandedDay, text)}
              onClose={() => setExpandedDay(null)}
              onAccountClick={onAccountClick}
              getUsableStrategies={getUsableStrategies}
              onOpenStrategyModal={onOpenStrategyModal}
            />
          )}
        </div>
        {monthWeeksSummary.length > 0 && (
          <div className="mth-calendar-sidebar">
            <h4 className="mth-calendar-sidebar-title">Weekly Summary</h4>
            {monthWeeksSummary.map((w, i) => (
              <div key={i} className="mth-calendar-week-item">
                <span className="mth-calendar-week-label">{w.label}</span>
                <span className={`mth-calendar-week-value ${w.total >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(w.total)}
                </span>
                <span className="mth-calendar-week-days">
                  {(w.daysCount ?? 0)} day{(w.daysCount ?? 0) !== 1 ? 's' : ''}
                </span>
              </div>
            ))}
          </div>
        )}
        </div>
      ) : (
        /* Year View - no sidebar */
        <div className="mth-calendar-year-grid">
          {monthNames.map((name, idx) => {
            const stats = yearStats[idx];
            const hasData = stats.days > 0;
            const tone = hasData ? (stats.totalPnl >= 0 ? 'profit' : 'loss') : '';

            return (
              <div
                key={idx}
                className={`mth-year-cell ${tone}`}
                onClick={() => {
                  setCalendarMonth(prev => ({ ...prev, month: idx }));
                  setCalendarRange('month');
                  setExpandedDay(null);
                }}
              >
                <span className="mth-year-month">{name}</span>
                <span className={`mth-year-value ${hasData ? (stats.totalPnl >= 0 ? 'positive' : 'negative') : 'empty'}`}>
                  {hasData ? formatCompactCurrency(stats.totalPnl) : '—'}
                </span>
                <span className="mth-year-info">
                  {hasData ? `${stats.trades} trades · ${stats.days} days` : '0 days'}
                </span>
              </div>
            );
          })}
        </div>
      )}
        </>
      )}
    </div>
  );
}

// Sub-component: Expanded day detail showing per-account breakdown
function DayDetail({ dateKey, dayData, formatCurrency, formatDate, actualDayAlerts, connectionMap, dayNote, onDayNoteChange, onClose, onAccountClick, getUsableStrategies = null, onOpenStrategyModal = null }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('pnl');
  const [sortDirection, setSortDirection] = useState('desc');

  const toDateKey = useCallback((value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const isSnapshotDay = useMemo(() => {
    return Object.values(connectionMap || {}).some((conn) => toDateKey(conn?.last_sync_at) === dateKey);
  }, [connectionMap, dateKey, toDateKey]);

  const getDisplayPnl = useCallback((acctName, fallbackPnl) => {
    if (!isSnapshotDay) return fallbackPnl;
    const realized = Number(connectionMap?.[acctName]?.realized_pnl);
    return Number.isFinite(realized) ? realized : fallbackPnl;
  }, [connectionMap, isSnapshotDay]);

  const allAccounts = useMemo(() => {
    return Object.entries(dayData.accountBreakdown)
      .map(([acctName, data]) => ({
        acctName,
        ...data,
        displayPnl: getDisplayPnl(acctName, data.pnl)
      }));
  }, [dayData, getDisplayPnl]);

  const filteredAndSortedAccounts = useMemo(() => {
    let list = allAccounts;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      list = list.filter(a => {
        const conn = connectionMap[a.acctName];
        const strat = conn?.strategies?.[0]?.strategy_name || conn?.last_strategy?.strategy_name || '';
        const inst = [...(a.instruments || [])].join(' ').toLowerCase();
        return a.acctName.toLowerCase().includes(q) || strat.toLowerCase().includes(q) || inst.includes(q);
      });
    }
    const col = sortColumn;
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (col === 'account') return dir * (a.acctName.localeCompare(b.acctName));
      if (col === 'strategy') {
        const sa = connectionMap[a.acctName]?.strategies?.[0]?.strategy_name || connectionMap[a.acctName]?.last_strategy?.strategy_name || '';
        const sb = connectionMap[b.acctName]?.strategies?.[0]?.strategy_name || connectionMap[b.acctName]?.last_strategy?.strategy_name || '';
        return dir * sa.localeCompare(sb);
      }
      if (col === 'instrument') {
        const ia = [...(a.instruments || [])].join(',');
        const ib = [...(b.instruments || [])].join(',');
        return dir * ia.localeCompare(ib);
      }
      if (col === 'cash_value') {
        const ca = parseFloat(connectionMap[a.acctName]?.starting_balance) || 0;
        const cb = parseFloat(connectionMap[b.acctName]?.starting_balance) || 0;
        return dir * (ca - cb);
      }
      if (col === 'trailing_drawdown') {
        const ta = parseFloat(connectionMap[a.acctName]?.trailing_max_drawdown) || 0;
        const tb = parseFloat(connectionMap[b.acctName]?.trailing_max_drawdown) || 0;
        return dir * (ta - tb);
      }
      if (col === 'pnl') return dir * (a.displayPnl - b.displayPnl);
      return 0;
    });
  }, [allAccounts, searchTerm, sortColumn, sortDirection, connectionMap]);

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortColumn(col);
      setSortDirection(['pnl', 'cash_value', 'trailing_drawdown'].includes(col) ? 'desc' : 'asc');
    }
  };

  const totalWinRate = dayData.trades > 0
    ? ((dayData.winningTrades / dayData.trades) * 100).toFixed(1)
    : '0.0';

  const displayedDayTotal = useMemo(() => {
    if (!isSnapshotDay) return dayData.totalPnl;
    return Object.entries(dayData.accountBreakdown).reduce((sum, [acctName, acctData]) => {
      if (connectionMap?.[acctName]?.exclude_from_metrics) return sum;
      return sum + getDisplayPnl(acctName, acctData.pnl);
    }, 0);
  }, [isSnapshotDay, dayData, connectionMap, getDisplayPnl]);

  return (
    <div className="mth-day-detail">
      <div className="mth-day-detail-header">
        <div>
          <h4 className="mth-day-detail-title">{formatDate(dateKey)}</h4>
          <div className="mth-day-detail-summary">
            <span className={`mth-day-detail-pnl ${displayedDayTotal >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(displayedDayTotal)}
            </span>
            <span className="mth-day-detail-info">
              {dayData.trades} trades · {dayData.accounts.size} accounts · {totalWinRate}% win rate
            </span>
          </div>
        </div>
        <button className="mth-day-detail-close" onClick={onClose}>✕</button>
      </div>

      <div className="mth-day-detail-body">
        <div className="mth-day-detail-accounts">
          <div className="mth-day-controls">
            <input
              type="text"
              placeholder="Search accounts or strategies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mth-day-accounts-search"
            />
            <div className="mth-day-sort">
              <span className="mth-day-sort-label">Sort:</span>
              {[
                { key: 'pnl', label: 'P&L' },
                { key: 'account', label: 'Account' },
                { key: 'strategy', label: 'Strategy' },
                { key: 'instrument', label: 'Instrument' },
                { key: 'cash_value', label: 'Cash' },
                { key: 'trailing_drawdown', label: 'DD' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  className={`mth-day-sort-btn ${sortColumn === key ? 'active' : ''}`}
                  onClick={() => handleSort(key)}
                >
                  {label} {sortColumn === key && (sortDirection === 'asc' ? '↑' : '↓')}
                </button>
              ))}
            </div>
          </div>
          <div className="mth-day-accounts-list">
            {filteredAndSortedAccounts.map(acct => {
              const alert = actualDayAlerts?.[dateKey]?.[acct.acctName];
              const conn = connectionMap[acct.acctName];
              const usableStrategies = getUsableStrategies && conn ? getUsableStrategies(conn) : [];
              const hasMultipleStrats = usableStrategies.length >= 2;
              const strategyDisplay = hasMultipleStrats
                ? 'Multiple Strats'
                : (conn?.strategies?.[0]?.strategy_name || conn?.last_strategy?.strategy_name || null);
              const winRate = acct.trades > 0 ? ((acct.wins / acct.trades) * 100).toFixed(0) : '0';

              return (
                <div
                  key={acct.acctName}
                  className={`mth-account-row ${onAccountClick ? 'mth-account-row-clickable' : ''}`}
                  onClick={onAccountClick ? () => onAccountClick(acct.acctName, dateKey) : undefined}
                  onKeyDown={onAccountClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAccountClick(acct.acctName, dateKey); } } : undefined}
                  role={onAccountClick ? 'button' : undefined}
                  tabIndex={onAccountClick ? 0 : undefined}
                  title={onAccountClick ? 'Click to view trades for this day' : undefined}
                >
                  <div className="mth-account-info">
                    <div className="mth-account-name-row">
                      <span className="mth-account-name">{acct.acctName}</span>
                      {conn?.exclude_from_metrics ? (
                        <span className="mth-alert-badge excluded" title="Excluded from metrics in Accounts view">EXCLUDED</span>
                      ) : (
                        <>
                          {alert?.blowout && <span className="mth-alert-badge blowout" title="Trailing max drawdown breached this day">BLOWOUT</span>}
                          {alert?.thirtyRule && <span className="mth-alert-badge thirty-pct" title="A trade exceeded the consistency % limit this day">% RULE</span>}
                        </>
                      )}
                    </div>
                    <div className="mth-account-meta">
                      {strategyDisplay && (
                        <span
                          className="mth-strategy-pill"
                          style={hasMultipleStrats && onOpenStrategyModal ? { cursor: 'pointer', textDecoration: 'underline' } : {}}
                          onClick={hasMultipleStrats && onOpenStrategyModal ? (e) => {
                            e.stopPropagation();
                            onOpenStrategyModal(usableStrategies, acct.acctName);
                          } : undefined}
                          title={hasMultipleStrats && onOpenStrategyModal ? `Click to view ${usableStrategies.length} strategies` : undefined}
                          role={hasMultipleStrats && onOpenStrategyModal ? 'button' : undefined}
                        >
                          {strategyDisplay}
                        </span>
                      )}
                      {[...(acct.instruments || [])].map(inst => (
                        <span key={inst} className="mth-instrument-pill">{inst}</span>
                      ))}
                      <span className="mth-trade-count">{acct.trades} trade{acct.trades !== 1 ? 's' : ''} · {winRate}% WR</span>
                    </div>
                    {(alert?.accountSize != null || alert?.maxDrawdown != null || conn?.starting_balance != null || (conn?.trailing_max_drawdown != null && parseFloat(conn.trailing_max_drawdown) > 0)) && (
                      <div className="mth-account-tier-info">
                        {(conn?.starting_balance != null || alert?.accountSize != null) && (
                          <span>Acct: {formatCurrency(conn?.starting_balance ?? alert?.accountSize ?? 0)}</span>
                        )}
                        {(alert?.maxDrawdown != null || (conn?.trailing_max_drawdown != null && parseFloat(conn.trailing_max_drawdown) > 0)) && (
                          <span>Max DD: {formatCurrency(alert?.maxDrawdown ?? conn?.trailing_max_drawdown ?? 0)}</span>
                        )}
                        {alert?.blowout && alert?.minEquity != null && (
                          <span className="mth-breach-detail">Low: {formatCurrency(alert.minEquity)} &lt; {formatCurrency(alert.blowoutThreshold)}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className={`mth-account-pnl ${acct.displayPnl >= 0 ? 'positive' : 'negative'}`}>
                    {formatCurrency(acct.displayPnl)}
                  </div>
                </div>
              );
            })}
          </div>
          {filteredAndSortedAccounts.length === 0 && (
            <p className="mth-day-table-empty">
              {searchTerm.trim() ? 'No accounts match your search' : 'No account data'}
            </p>
          )}
        </div>
        <div className="mth-day-detail-notes">
          <label className="mth-day-detail-notes-label">Notes for this day</label>
          <DayNotesEditor
            dateKey={dateKey}
            value={dayNote ?? ''}
            onChange={onDayNoteChange}
          />
        </div>
      </div>
    </div>
  );
}

export default MyTradeHistory;
